import React from "react";
import { Box, Container, Grid, Typography, Button } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { uploadLink } from "../../utils/constants";
import { StoreThemeTokens, getStoreTheme } from "./themes/storeThemes";
import { getWhatsAppUrl } from "../../utils/whatsappHelper";

interface StoreHeroProps {
  storeData?: any;
  products?: any[];
  themeTokens?: StoreThemeTokens;
}

const StoreHero: React.FC<StoreHeroProps> = ({
  storeData,
  products = [],
  themeTokens: propTokens,
}) => {
  const themeTokens = propTokens || getStoreTheme(storeData?.theme);
  const hero = storeData?.hero || {};

  const headline = hero.headline || "Your Everyday";
  const headlineItalic = hero.headline_italic || "Self-Care Companion";
  const description =
    hero.description ||
    storeData?.description ||
    "Elevate your daily ritual with salon-grade hair stylers and rejuvenating smart massagers, designed for timeless grace and wellness.";
  const badgeText = hero.badge_text || "Elegance Meets Care";
  const quoteScript = hero.quote_script || "Care Looks Good On You ♡";
  const tags: string[] =
    hero.tags && hero.tags.length > 0
      ? hero.tags
      : ["🪷 Beauty", "♡ Wellness", "🍃 Better You"];
  const primaryCtaLabel = hero.primary_cta_label || "Shop Best Sellers";
  const whatsappCtaLabel = hero.whatsapp_cta_label || "Order via WhatsApp";
  const cardTitle = hero.card_title || "Beauty & Personal Therapy";
  const cardSubtitle = hero.card_subtitle || "Featured Collection";
  const cardDeliveryText =
    hero.card_delivery_text || "Direct Delivery in Thane & All-India";
  const showDottedPattern =
    hero.show_dotted_pattern !== undefined
      ? Boolean(hero.show_dotted_pattern)
      : true;

  // Showcase image: check hero banner_image, then first product image, then store image, then fallback
  let showcaseImage = "";
  if (hero.banner_image) {
    showcaseImage =
      hero.banner_image.startsWith("http") || hero.banner_image.startsWith("data:")
        ? hero.banner_image
        : `${uploadLink}${hero.banner_image.replace(/^\//, "")}`;
  } else if (products.length > 0 && products[0]?.images?.length > 0) {
    const firstImg = products[0].images[0];
    showcaseImage = firstImg.startsWith("http") ? firstImg : `${uploadLink}${firstImg}`;
  } else if (storeData?.images && storeData.images.length > 0) {
    const firstStoreImg = storeData.images[0];
    showcaseImage = firstStoreImg.startsWith("http")
      ? firstStoreImg
      : `${uploadLink}${firstStoreImg}`;
  } else {
    showcaseImage =
      "https://lh3.googleusercontent.com/aida-public/AB6AXuBd8oTdc4sDTSYcLd_ZL7PAFOGf4Lrgl8rughRwqiUhzaSBgLQlHPfjYPSXisVEM46j73yXXGme9MPp0tQE_WANlFHFCDTQGyEg61SYh5O8srx7zZ7ZQ4C9ofvsfA3fEYWybImJGGdgCl9X2OgkjyG2Syj7Hzr-WEC8xAjLz3LeKhSgyBxcQccgIq1JQ6C2fg3FI8FoXYd0P1QZjQ773gTsHASNRTjuiVX0uuVCJ7eF_aymti2YMbiLUOWnFMP7yEX3sI4";
  }

  const handleScrollToProducts = () => {
    const section = document.getElementById("products-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleWhatsAppOrder = () => {
    const phone = storeData?.number || storeData?.header?.phone_number;
    if (phone) {
      const waUrl = getWhatsAppUrl(
        phone,
        `Hi ${storeData?.name || "RUDRIX"}, I'd like to order products`
      );
      if (waUrl) window.open(waUrl, "_blank");
    }
  };

  // 1. Badge Pill
  const renderBadgePill = () => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 0.75,
        borderRadius: 999,
        bgcolor: "rgba(255,255,255,0.85)",
        border: `1px solid ${themeTokens.accent}40`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        mb: { xs: 2, lg: 3 },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: themeTokens.accent,
          fontSize: "0.75rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}
      >
        {badgeText}
      </Typography>
    </Box>
  );

  // 2 & 3. Main Headline & Italic Highlight
  const renderHeadlines = (isMobile = false) => (
    <Typography
      variant="h1"
      sx={{
        fontFamily: themeTokens.fontSerif,
        fontSize: isMobile
          ? { xs: "2.1rem", sm: "2.8rem" }
          : { lg: "4.1rem" },
        fontWeight: 600,
        color: themeTokens.textPrimary,
        lineHeight: 1.12,
        letterSpacing: "-0.02em",
        mb: isMobile ? 1.75 : 2.5,
      }}
    >
      {headline}
      <br />
      <Box
        component="span"
        sx={{
          fontStyle: "italic",
          fontWeight: 500,
          color: themeTokens.accent,
          display: "inline-block",
        }}
      >
        {headlineItalic}
      </Box>
    </Typography>
  );

  // 4. Cursive Script Accent Quote (Mobile version)
  const renderCursiveQuoteMobile = () =>
    quoteScript ? (
      <Box
        sx={{
          mb: 3,
          userSelect: "none",
          transform: "rotate(-2deg)",
        }}
      >
        <Typography
          sx={{
            fontFamily: themeTokens.fontScript,
            fontSize: { xs: "1.75rem", sm: "2.2rem" },
            fontWeight: 700,
            color: themeTokens.accent,
            lineHeight: 1.15,
            textShadow: "0 2px 8px rgba(255,255,255,0.8)",
          }}
        >
          {quoteScript}
        </Typography>
      </Box>
    ) : null;

  // 4. Cursive Script Accent Quote (Desktop floating version)
  const renderCursiveQuoteDesktop = () =>
    quoteScript ? (
      <Box
        sx={{
          position: "absolute",
          top: -34,
          right: 24,
          transform: "rotate(3deg)",
          zIndex: 10,
          textAlign: "right",
          userSelect: "none",
        }}
      >
        <Typography
          sx={{
            fontFamily: themeTokens.fontScript,
            fontSize: "2.8rem",
            fontWeight: 700,
            color: themeTokens.accent,
            lineHeight: 1.1,
            textShadow: "0 2px 8px rgba(255,255,255,0.8)",
          }}
        >
          {quoteScript}
        </Typography>
      </Box>
    ) : null;

  // 5. Hero Image Showcase Card
  const renderShowcaseCard = () => (
    <Box
      sx={{
        position: "relative",
        zIndex: 2,
        width: "100%",
        maxWidth: 440,
        mx: "auto",
        borderRadius: 4,
        overflow: "hidden",
        border: "4px solid #FFFFFF",
        bgcolor: "#FFFFFF",
        boxShadow: "0 20px 40px -15px rgba(0,0,0,0.12)",
        transition: "transform 0.4s ease",
        "&:hover": {
          transform: "scale(1.02)",
        },
      }}
    >
      <Box sx={{ position: "relative", height: { xs: 260, sm: 320 }, overflow: "hidden" }}>
        <img
          src={showcaseImage}
          alt={cardTitle}
          crossOrigin="anonymous"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "top",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)",
          }}
        />
        <Box sx={{ position: "absolute", bottom: 16, left: 16, right: 16, color: "#FFFFFF", textAlign: "left" }}>
          <Typography
            variant="caption"
            sx={{
              color: "#fbbf24",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontSize: "0.72rem",
            }}
          >
            {cardSubtitle}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: themeTokens.fontSerif,
              fontWeight: 600,
              fontSize: { xs: "1.2rem", sm: "1.35rem" },
              lineHeight: 1.25,
            }}
          >
            {cardTitle}
          </Typography>
        </Box>
      </Box>

      {/* Bottom Card Footer */}
      <Box
        sx={{
          p: 2,
          bgcolor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "0.75rem",
          color: themeTokens.textSecondary,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981" }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: themeTokens.textPrimary }}>
            {cardDeliveryText}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: themeTokens.accent }}>
          Verified Quality
        </Typography>
      </Box>
    </Box>
  );

  // 6. Feature Badges / Wellness Pills
  const renderTags = (isMobile = false) =>
    tags && tags.length > 0 ? (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: isMobile ? "center" : { xs: "center", lg: "flex-start" },
          gap: 1.5,
          mb: isMobile ? 3 : 4,
        }}
      >
        {tags.map((tag, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(6px)",
              px: 2,
              py: 0.8,
              borderRadius: 999,
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              border: `1px solid ${themeTokens.cardBorder}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: themeTokens.textPrimary,
                letterSpacing: "0.04em",
              }}
            >
              {tag}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : null;

  // 7 & 8. Action Buttons (Primary CTA & WhatsApp CTA)
  const renderButtons = (isMobile = false) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: isMobile ? "center" : { xs: "center", lg: "flex-start" },
        gap: 2,
        width: isMobile ? "100%" : "auto",
        maxWidth: isMobile ? 360 : "none",
        mx: isMobile ? "auto" : 0,
      }}
    >
      <Button
        variant="contained"
        onClick={handleScrollToProducts}
        endIcon={<ArrowForwardIcon />}
        sx={{
          width: { xs: "100%", sm: "auto" },
          bgcolor: themeTokens.bgFooter,
          color: "#FFFFFF",
          px: 4,
          py: 1.5,
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.92rem",
          letterSpacing: "0.02em",
          boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
          "&:hover": {
            bgcolor: "#2D2825",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          },
          transition: "all 0.25s ease",
        }}
      >
        {primaryCtaLabel}
      </Button>

      <Button
        variant="outlined"
        onClick={handleWhatsAppOrder}
        startIcon={<WhatsAppIcon sx={{ color: "#16a34a" }} />}
        sx={{
          width: { xs: "100%", sm: "auto" },
          bgcolor: "#FFFFFF",
          color: themeTokens.textPrimary,
          borderColor: themeTokens.cardBorder,
          px: 3.5,
          py: 1.5,
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.92rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          "&:hover": {
            bgcolor: "#fafafa",
            borderColor: themeTokens.accent,
            transform: "translateY(-2px)",
          },
          transition: "all 0.25s ease",
        }}
      >
        {whatsappCtaLabel}
      </Button>
    </Box>
  );

  return (
    <Box
      component="section"
      id="hero-banner"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: themeTokens.heroGradient,
        borderBottom: `1px solid ${themeTokens.cardBorder}`,
        py: { xs: 5, sm: 8, md: 10, lg: 12 },
      }}
    >
      {/* 1. Dotted / Beaded Background Pattern matching code.html */}
      {showDottedPattern && (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(circle, ${themeTokens.accent || "#C5896E"} 1.75px, transparent 2.25px)`,
            backgroundSize: "8px 8px",
            opacity: 0.18,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}

      {/* 2. Hero Radial Glow Accent matching code.html */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 65% 50%, rgba(243, 229, 221, 0.7) 0%, rgba(253, 249, 245, 0.25) 70%, transparent 100%)`,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        {/* MOBILE VIEW (xs to md) - Ordered strictly:
            1. Badge Pill Text
            2. Main Headline
            3. Italic Headline Highlight
            4. Cursive Script Accent Quote
            5. Hero Image
            6. Feature Badges / Wellness Pills (Comma separated)
            7. Primary Button Label
            8. WhatsApp Order Button Label
        */}
        <Box
          sx={{
            display: { xs: "flex", lg: "none" },
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* 1. Badge Pill Text */}
          {renderBadgePill()}

          {/* 2 & 3. Main Headline & Italic Highlight */}
          {renderHeadlines(true)}

          {/* 4. Cursive Script Accent Quote */}
          {renderCursiveQuoteMobile()}

          {/* 5. Hero Image */}
          <Box sx={{ position: "relative", width: "100%", mb: 3.5 }}>
            <Box
              sx={{
                width: { xs: 240, sm: 340 },
                height: { xs: 240, sm: 340 },
                borderRadius: "50%",
                background: `radial-gradient(circle, ${themeTokens.accentLight} 0%, rgba(253, 249, 245, 0.2) 70%, transparent 100%)`,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                filter: "blur(40px)",
                opacity: 0.8,
                zIndex: 0,
              }}
            />
            {renderShowcaseCard()}
          </Box>

          {/* 6. Feature Badges / Wellness Pills */}
          {renderTags(true)}

          {/* 7 & 8. Action Buttons (Primary CTA & WhatsApp CTA) */}
          {renderButtons(true)}
        </Box>

        {/* DESKTOP VIEW (lg and above) - 2-Column Grid */}
        <Box sx={{ display: { xs: "none", lg: "block" } }}>
          <Grid container spacing={6} alignItems="center">
            {/* Left Text Content */}
            <Grid item xs={12} lg={6} sx={{ textAlign: "left" }}>
              {renderBadgePill()}
              {renderHeadlines(false)}
              <Typography
                variant="body1"
                sx={{
                  color: themeTokens.textSecondary,
                  fontSize: "1.05rem",
                  lineHeight: 1.65,
                  fontWeight: 400,
                  maxWidth: 480,
                  mb: 3.5,
                }}
              >
                {description}
              </Typography>
              {renderTags(false)}
              {renderButtons(false)}
            </Grid>

            {/* Right Visual: Showcase Card with Floating Quote */}
            <Grid item xs={12} lg={6} sx={{ position: "relative" }}>
              {renderCursiveQuoteDesktop()}
              <Box
                sx={{
                  width: 340,
                  height: 340,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${themeTokens.accentLight} 0%, rgba(253, 249, 245, 0.2) 70%, transparent 100%)`,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  filter: "blur(40px)",
                  opacity: 0.8,
                  zIndex: 0,
                }}
              />
              {renderShowcaseCard()}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default StoreHero;
